import { Badge, useBreakpoint } from 'slab'
import { getFormattedDate } from 'utils'

import { isPayScheduleActive } from './utils'
import { type PaySchedule } from '../../types/payroll'

export const PayScheduleStatusBadge = ({ paySchedule }: { paySchedule: PaySchedule }) => {
  const scheduleActive = isPayScheduleActive(paySchedule)

  const formattedStartDate = getFormattedDate(paySchedule.start_date, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

  const formattedEndDate = paySchedule.end_date ? getFormattedDate(paySchedule.end_date, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : ''

  // Check if the schedule is verified
  const isVerified = paySchedule.is_verified

  // Determine the badge type and text based on verification and active status
  const getBadgeState = () => {
    // If deactivated, show deactivated regardless of verification status
    if (!scheduleActive) {
      return {
        type: 'critical' as const,
        text: 'Deactivated',
        description: `Deactivated since ${formattedEndDate}`,
        shortDescription: `since ${formattedEndDate}`
      }
    }

    // If active, show either Active (verified) or Unverified (not verified)
    return isVerified
      ? {
          type: 'success' as const,
          text: 'Active',
          description: `Active since ${formattedStartDate}`,
          shortDescription: `since ${formattedStartDate}`
        }
      : {
          type: 'caution' as const,
          text: 'Unverified',
          description: 'Not verified yet',
          shortDescription: ''
        }
  }

  const badgeState = getBadgeState()

  const { breakpointIsAtLeast } = useBreakpoint()

  return (
    <div className='flex flex-row items-center xs:flex-col xs:items-end gap-1'>
      <div className='w-fit'>
        <Badge type={badgeState.type}>{badgeState.text}</Badge>
      </div>
      <span className='text-sm text-gray-500'>
        {breakpointIsAtLeast('xs') ? badgeState.description : badgeState.shortDescription}
      </span>
    </div>
  )
}
